import {
  NumericInputDirective,
  parseNumericInput,
} from '@fullyops/shared/directives/numeric-input.directive';
import {
  booleanAttribute,
  Component,
  Injector,
  ViewChild,
  afterNextRender,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormControl,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MaterialModule } from '@fullyops/shared/material.module';
import moment, { Moment } from 'moment-timezone';
import {
  MomentDateAdapter,
  MAT_MOMENT_DATE_FORMATS,
} from '@angular/material-moment-adapter';
import { MAT_DATE_FORMATS, DateAdapter } from '@angular/material/core';
import { ZonedMomentDateAdapter } from '@fullyops/shared/zoned-moment-date-adapter';
import {
  Attachment,
  AttachmentAction,
  ALL_FILE_TYPES,
  FileUploadComponent,
} from '@fullyops/shared/components/file-upload/component';
import {
  combineLatest,
  filter,
  map,
  merge,
  Observable,
  of,
  startWith,
  Subscription,
  switchMap,
} from 'rxjs';
import { MomentTimezoneToAngularTimezonePipe } from '@fullyops/shared/pipes/moment-timezone-to-angular-timezone.pipe';
import { RouterModule } from '@angular/router';
import {
  AssetClass,
  assetClasses,
  AssetTypeField,
  ListedAssetType,
} from '@fullyops/data/asset-types/asset-type';
import {
  assetCriticalities,
  AssetCriticality,
  BaseAssetInput,
  MiniAsset,
  StandaloneEquipmentInput,
} from '@fullyops/data/assets/asset';
import { ApiAssetTypesAdapterService } from '@fullyops/data/asset-types/api-adapter.service';
import { CommonModule } from '@angular/common';
import { MiniCompany } from '@fullyops/data/companies/mini-company';
import { MiniLocation } from '@fullyops/data/locations/location';
import { AssetCategory } from '@fullyops/data/asset-categories/asset-category';
import { DateTimeInputComponent } from '@fullyops/shared/components/date-time-input/date-time-input.component';
import { AutocompletingSelectComponent } from '@fullyops/shared/components/autocompleting-select/component';
import { ArrowComponent } from '@fullyops/shared/components/arrow/component';
import { AssetFormTranslatePipe } from '../../translations/translate.pipe';
import {
  FormSectionComponent,
  FormSectionStatus,
} from '@fullyops/shared/components/form-section/form-section.component';
import { SectionedFormComponent } from '@fullyops/shared/components/sectioned-form/sectioned-form.component';
import { PlusButtonComponent } from '@fullyops/shared/components/plus-button/plus-button.component';
import { SidenavPortalBridgeService } from '@fullyops/core/sidenav-form-portal-bridge.service';
import { AssetCategoriesService } from '@fullyops/domain/asset-categories/asset-categories.service';
import { PermissionType, UiAuthService } from '@fullyops/legacy/ui/index';
import { normalizeForSearch } from '@fullyops/shared/normalize-for-search';
import {
  ASSET_CATEGORY_SIDEBAR_TOKEN,
  AssetCategorySidebarComponent,
} from '@fullyops/features/configuration/containers/asset-categories/asset-category-sidebar/asset-category-sidebar.component';

@Component({
  selector: 'fo-equipment-form',
  imports: [
    CommonModule,
    MaterialModule,
    ReactiveFormsModule,
    RouterModule,
    FileUploadComponent,
    AutocompletingSelectComponent,
    ArrowComponent,
    AssetFormTranslatePipe,
    MomentTimezoneToAngularTimezonePipe,
    DateTimeInputComponent,
    FormSectionComponent,
    SectionedFormComponent,
    PlusButtonComponent,
    NumericInputDirective,
  ],
  templateUrl: './equipment-form.component.html',
  styleUrl: './equipment-form.component.scss',
  providers: [
    // MomentDateAdapter isn't providedIn: root, so we must inject it here so
    // that ZonedMomentDateAdapter can delegate to it.
    MomentDateAdapter,
    // Since we're not using MomentDateAdapter directly, we must specify the
    // date formats ourselves.
    { provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS },
    { provide: DateAdapter, useClass: ZonedMomentDateAdapter },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline', floatLabel: 'always' },
    },
  ],
})
export class EquipmentFormComponent implements OnInit, OnChanges, OnDestroy {
  private assetTypeApi = inject(ApiAssetTypesAdapterService);
  private sidebar = inject(SidenavPortalBridgeService);
  private categoryService = inject(AssetCategoriesService);

  private authService = inject(UiAuthService);
  private injector = inject(Injector);

  protected canCreateCategory = this.authService.hasPermissionNoAsync([
    PermissionType.CAN_CREATE_ASSET_CATEGORIES,
  ]);
  private categoryNames = new Set<string>();
  private categoryPanelOpen = false;

  @Input() disabled = false;
  @Input() createdAt?: string;
  @Input() initialValue?: BaseAssetInput;
  @Input() showCompany = false;
  /**
   * Whether the owner — the company, and the location that implies one — can be changed here. When
   * false both are shown read-only: seeing the owner is governed by access to companies, while
   * changing it needs CAN_MANAGE_EQUIPMENT_OWNERSHIP and the allowManualOwnershipChanges tenant
   * configuration. The unchanged owner is still emitted, which the back end accepts.
   */
  @Input() set editableCompany(editable: boolean) {
    this._editableCompany = editable;
    this.applyOwnerEditability();
  }
  get editableCompany(): boolean {
    return this._editableCompany;
  }
  private _editableCompany = true;
  @Input() showActive = false;
  @Input() companyChoices$: Observable<MiniCompany[]> = of([]);
  @Input() parentChoices$: Observable<MiniAsset[]> = of([]);
  @Input() locationChoices$: Observable<MiniLocation[]> = of([]);
  @Input() categoryChoices$: Observable<AssetCategory[]> = of([]);
  @Input() assetTypeChoices: ListedAssetType[] = [];
  /**
   * For creating: the asset type is chosen on its own first, and the rest of the form, which depends on it, is
   * only shown once it is.
   */
  @Input({ transform: booleanAttribute }) typeFirst = false;
  /** Whether the sections are listed in an index beside them, or in a drawer on a narrow form. */
  @Input({ transform: booleanAttribute }) showSectionIndex = true;
  @Output() valueChange = new EventEmitter<BaseAssetInput>();
  @Input() attachments: Attachment[] = [];
  @Output() newAttachmentsChange = new EventEmitter<AttachmentAction>();

  get dirty() {
    return (
      this.baseFormGroup.dirty ||
      this.classFormDirty ||
      this.customFormGroup.dirty
    );
  }

  get valid() {
    return (
      this.baseFormGroup.valid &&
      this.classFormValid &&
      this.customFormGroup.valid
    );
  }

  private sub = new Subscription();

  private _choosingType = false;
  /** Whether the form only shows the asset type choice; see `typeFirst`. */
  get choosingType(): boolean {
    return this._choosingType;
  }

  /** The type choices by class, in class order, leaving out classes with none. */
  protected typeGroups: { assetClass: AssetClass; types: ListedAssetType[] }[] =
    [];

  protected readonly acceptedFileTypes = ALL_FILE_TYPES;

  ngOnChanges(): void {
    this.typeGroups = assetClasses
      .map((assetClass) => ({
        assetClass,
        types: this.assetTypeChoices.filter((t) => t.assetClass === assetClass),
      }))
      .filter((g) => g.types.length > 0);
  }

  @ViewChild(SectionedFormComponent) private sectioned?: SectionedFormComponent;

  /** The sections shown for the current type, in order. */
  private get sections(): FormSection[] {
    return [
      'general',
      'ownershipAndLocation',
      ...(this.assetClass === 'STANDALONE_EQUIPMENT'
        ? (['equipmentDetails'] as const)
        : []),
      'installationAndWarranty',
      ...(this.customFields.length > 0
        ? (['customFieldsSection'] as const)
        : []),
      'attachments',
    ];
  }

  /**
   * When creating, how many sections are shown: they are gone through in turn, only the last one shown having
   * a Next button, which checks it and reveals the following one once it is valid.
   */
  private revealed = 1;

  /** The index of the last section shown, the type's sections possibly having changed since. */
  private get frontier(): number {
    return Math.min(this.revealed, this.sections.length) - 1;
  }

  protected isLocked(section: FormSection): boolean {
    return this.typeFirst && this.sections.indexOf(section) > this.frontier;
  }

  protected hasNext(section: FormSection): boolean {
    const i = this.sections.indexOf(section);
    return (
      this.typeFirst && i === this.frontier && i < this.sections.length - 1
    );
  }

  /** Moves on only once the section is valid; otherwise its fields show what is missing. */
  protected openNext(section: FormSection): void {
    const invalid = this.sectionControls(section).filter((c) => c.invalid);
    if (invalid.length > 0) {
      for (const c of invalid) {
        c.markAllAsTouched();
      }
      return;
    }
    const sections = this.sections;
    const i = sections.indexOf(section);
    this.revealed = Math.max(this.revealed, i + 2);
    // The section is only shown once rendered again.
    afterNextRender(() => this.sectioned?.scrollTo(sections[i + 1]), {
      injector: this.injector,
    });
  }

  /** Errors once a field has been touched; while creating, a section moved on from is done while valid. */
  protected sectionStatus(section: FormSection): FormSectionStatus {
    if (this.sectionErrors(section) > 0) {
      return 'error';
    }
    return this.typeFirst &&
      this.sections.indexOf(section) < this.frontier &&
      // A disabled control is neither valid nor invalid.
      this.sectionControls(section).every((c) => !c.invalid)
      ? 'done'
      : 'none';
  }

  protected sectionErrors(section: FormSection): number {
    return this.sectionControls(section).filter((c) => c.invalid && c.touched)
      .length;
  }

  private sectionControls(section: FormSection): AbstractControl[] {
    const base = this.baseFormGroup.controls;
    switch (section) {
      case 'general':
        return [
          base.assetType,
          base.name,
          base.active,
          base.externalId,
          base.criticality,
          base.categoryId,
          base.description,
        ];
      case 'ownershipAndLocation':
        return [base.companyId, base.location, base.parentId];
      case 'equipmentDetails':
        return [this.equipmentFormGroup];
      case 'installationAndWarranty':
        return [base.hasWarranty, base.warranty, base.installedAt];
      case 'customFieldsSection':
        return [this.customFormGroup];
      case 'attachments':
        return [];
    }
  }

  ngOnInit(): void {
    this._choosingType = this.typeFirst && this.initialValue?.typeId == null;

    let sub = merge(
      this.baseFormGroup.valueChanges,
      this.equipmentFormGroup.valueChanges,
      this.customFormGroup.valueChanges,
    ).subscribe(() => {
      const v = this.baseFormGroup.getRawValue();
      const custom = this.customFormGroup.getRawValue();

      this.valueChange.emit({
        typeId: v.assetType == null ? null : v.assetType.id,
        name: v.name,
        active: v.active,
        description: v.description,
        criticality: v.criticality,
        warrantyExpiresAt: toISOString(v.warranty),
        installedAt: toISOString(v.installedAt),
        externalId: v.externalId,
        categoryId: v.categoryId,
        companyId: v.companyId,
        locationId: v.location == null ? null : v.location.id,
        parentId: v.parentId,
        classSpecificProperties: this.classFormValue,
        typeSpecificProperties: Object.fromEntries(
          this.customFields.map((f, i) => [
            f.name,
            // booleans need a direct string conversion, dates (Moments) need a ISO string
            // conversion, everything else is already a string
            f.type === 'TIMESTAMP'
              ? (toISOString(custom.fields[i]) ?? '')
              : f.type === 'DATE'
                ? this.toIsoDate(custom.fields[i])
                : custom.fields[i].toString(),
          ]),
        ),
      });
    });

    this.sub.add(sub);

    this.sub.add(
      this.categoryChoices$.subscribe((categories) => {
        this.categoryNames = new Set(
          categories.map((c) => normalizeForSearch(c.name)),
        );
      }),
    );

    if (this.disabled) {
      this.baseFormGroup.disable({ emitEvent: false });
    }
    sub = this.baseFormGroup.controls.hasWarranty.valueChanges.subscribe(
      (has) => {
        if (!has) {
          this.baseFormGroup.controls.warranty.reset();
        }
        this.setWarrantyDisabled(!has);
      },
    );
    this.sub.add(sub);

    this.locations$ = combineLatest([
      this.baseFormGroup.controls.companyId.valueChanges.pipe(
        startWith(this.initialValue?.companyId),
      ),
      this.locationChoices$,
    ]).pipe(
      map(([cid, locs]) => {
        if (cid == null) {
          return [];
        }
        return locs.filter((loc) => loc.companyId === cid);
      }),
    );

    sub = this.baseFormGroup.controls.companyId.valueChanges
      .pipe(startWith(this.initialValue?.companyId))
      .subscribe((cid) => {
        if (cid == null) {
          this.baseFormGroup.controls.location.reset(null, {
            emitEvent: false,
          });
          this.baseFormGroup.controls.location.disable({ emitEvent: false });
        } else if (this.editableCompany && !this.disabled) {
          this.baseFormGroup.controls.location.enable({ emitEvent: false });
        }
      });
    this.sub.add(sub);

    // A location belongs to a single company, so the old one can't stay with a new company.
    sub = this.baseFormGroup.controls.companyId.valueChanges.subscribe(() =>
      this.baseFormGroup.controls.location.reset(null, { emitEvent: false }),
    );
    this.sub.add(sub);

    const v = this.initialValue;
    if (v != null) {
      this.baseFormGroup.patchValue(
        {
          assetType: v.typeId == null ? null : { id: v.typeId },
          name: v.name,
          active: v.active,
          description: v.description,
          criticality: v.criticality,
          warranty:
            v.warrantyExpiresAt == null ? null : moment(v.warrantyExpiresAt),
          hasWarranty: v.warrantyExpiresAt != null,
          installedAt: v.installedAt == null ? null : moment(v.installedAt),
          externalId: v.externalId,
          categoryId: v.categoryId,
          companyId: v.companyId,
          location: v.locationId == null ? null : { id: v.locationId },
          parentId: v.parentId,
        },
        { emitEvent: false },
      );
      this.setWarrantyDisabled(!this.baseFormGroup.controls.hasWarranty.value);
      if (v.typeId != null) {
        this.baseFormGroup.controls.assetType.disable({ emitEvent: false });
      }
    }
    this.applyOwnerEditability();

    sub = this.baseFormGroup.controls.assetType.valueChanges
      .pipe(
        // The asset type field can't be set to null by the user.
        map((atype) => atype!.id),
        startWith(this.initialValue?.typeId),
        filter((typeId) => typeId != null),
        switchMap((typeId) => {
          this.customFields = [];
          this.customFormGroup.controls.fields.clear({ emitEvent: false });
          return this.assetTypeApi.getById(typeId);
        }),
      )
      .subscribe((atype) => {
        const customFields = atype.fields.map((f) => ({
          ...f,
          readonly: false,
        }));

        const v = this.initialValue;
        if (v != null) {
          if (
            atype.assetClass === 'STANDALONE_EQUIPMENT' &&
            v.classSpecificProperties != null
          ) {
            const eq = v.classSpecificProperties as StandaloneEquipmentInput;
            this.equipmentFormGroup.setValue(
              {
                ...eq,
                manufacturedAt:
                  eq.manufacturedAt == null ? null : moment(eq.manufacturedAt),
              },
              { emitEvent: false },
            );
          }
          // need to show every value on this specific equipment even if the field has been removed from the type
          for (const p in v.typeSpecificProperties) {
            if (atype.fields.every((f) => f.name !== p)) {
              customFields.push({
                name: p,
                type: 'TEXT',
                required: false,
                readonly: true,
              });
            }
          }
        }

        this.assetClass = atype.assetClass;
        this.customFields = customFields;
        for (const f of customFields) {
          let defaultValue: unknown = '';
          switch (f.type) {
            case 'BOOLEAN':
              defaultValue = false;
              break;
            case 'DATE':
            case 'TIMESTAMP':
              defaultValue = null;
              break;
          }
          const control = new UntypedFormControl(defaultValue, {
            validators: [
              ...(f.required ? [Validators.required] : []),
              (ctl: AbstractControl) => outOfRange(ctl, f),
            ],
            nonNullable: true,
          });
          if (f.readonly) {
            control.disable({ emitEvent: false });
          }
          this.customFormGroup.controls.fields.push(control, {
            emitEvent: false,
          });
        }
        // so that the value change handler above triggers
        this.customFormGroup.updateValueAndValidity();

        if (v != null) {
          this.customFormGroup.setValue({
            fields: customFields.map((f) => {
              // A field may have been added since the asset was created, so we must account for
              // the possibility of a value not existing.
              const rawValue = v.typeSpecificProperties[f.name];
              switch (f.type) {
                case 'BOOLEAN':
                  return rawValue === 'true';
                case 'DATE':
                  return rawValue === '' || rawValue == null
                    ? null
                    : moment.tz(rawValue, 'YYYY-MM-DD', this.userTimeZone);
                case 'TIMESTAMP':
                  return rawValue === '' || rawValue == null
                    ? null
                    : moment(rawValue);
                default:
                  return rawValue ?? '';
              }
            }),
          });
        }
      });
    this.sub.add(sub);
  }

  // The picker shows days in the user's time zone, so that is the zone the stored calendar day is read in.
  private get userTimeZone(): string {
    return this.authService.currentUser.timeZone;
  }

  /** The field's limits as shown on its hint line: "0–500", "≥ 0" or "≤ 500". */
  protected fieldLimits(field: AssetTypeField): string {
    const { min, max } = field;
    if (min != null && max != null) {
      return `${min}–${max}`;
    }
    if (min != null) {
      return `≥ ${min}`;
    }
    return max != null ? `≤ ${max}` : '';
  }

  protected fieldHint(field: AssetTypeField): string {
    return [this.fieldLimits(field), field.description]
      .filter((part) => part != null && part !== '')
      .join(' · ');
  }

  private toIsoDate(m: Moment | null): string {
    return m == null
      ? ''
      : m.clone().tz(this.userTimeZone).format('YYYY-MM-DD');
  }

  clear(): void {
    this.baseFormGroup.reset(undefined, { emitEvent: false });
    if (this.typeFirst) {
      this.equipmentFormGroup.reset(undefined, { emitEvent: false });
      this.customFields = [];
      this.customFormGroup.controls.fields.clear({ emitEvent: false });
      this.assetClass = undefined;
      this._choosingType = true;
      this.revealed = 1;
    }
  }

  protected get chosenType(): ListedAssetType | null {
    return this.baseFormGroup.controls.assetType.value as ListedAssetType;
  }

  /** Only the fields every type has are kept when another type is chosen. */
  protected chooseType(type: ListedAssetType): void {
    if (type.id !== this.chosenType?.id) {
      this.equipmentFormGroup.reset(undefined, { emitEvent: false });
      this.baseFormGroup.controls.assetType.setValue(type);
    }
    this._choosingType = false;
  }

  protected changeType(): void {
    this._choosingType = true;
  }

  protected idEquals(
    a: { id: string } | null,
    b: { id: string } | null,
  ): boolean {
    return a?.id === b?.id;
  }

  private setWarrantyDisabled(disabled: boolean) {
    if (disabled) {
      this.baseFormGroup.controls.warranty.disable({ emitEvent: false });
    } else {
      this.baseFormGroup.controls.warranty.enable({ emitEvent: false });
    }
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    if (this.categoryPanelOpen) {
      this.sidebar.popPortal();
    }
  }

  /** Creates a category in a panel, on top of the sidebar when this form is in it, and picks it. */
  protected createCategory(): void {
    const nested = this.sidebar.isOpen;
    const close = () => {
      this.categoryPanelOpen = false;
      this.sidebar.popPortal();
    };
    this.sidebar.pushPortal(
      AssetCategorySidebarComponent,
      ASSET_CATEGORY_SIDEBAR_TOKEN,
      {
        existingNames: this.categoryNames,
        nested,
        onCancel: close,
        onSave: (name) => {
          close();
          this.categoryService.create({ name }).subscribe((id) => {
            const category = this.baseFormGroup.controls.categoryId;
            category.setValue(id);
            category.markAsDirty();
          });
        },
      },
    );
    this.categoryPanelOpen = true;
  }

  protected criticalities = assetCriticalities;

  protected locations$: Observable<MiniLocation[]>;

  protected baseFormGroup = new FormGroup({
    assetType: new FormControl<{ id: string } | null>(
      null,
      Validators.required,
    ),
    name: new FormControl('', {
      nonNullable: true,
      validators: Validators.required,
    }),
    active: new FormControl(true, { nonNullable: true }),
    description: new FormControl('', { nonNullable: true }),
    criticality: new FormControl<AssetCriticality | null>(null),
    installedAt: new FormControl<Moment | null>(null),
    hasWarranty: new FormControl(false, { nonNullable: true }),
    warranty: new FormControl<Moment | null>(null),
    externalId: new FormControl('', { nonNullable: true }),
    categoryId: new FormControl<string | null>(null),
    companyId: new FormControl<string | null>(null),
    location: new FormControl<{ id: string } | null>(null),
    parentId: new FormControl<string | null>(null),
  });

  protected equipmentFormGroup = new FormGroup({
    brand: new FormControl('', { nonNullable: true }),
    model: new FormControl('', { nonNullable: true }),
    serialNumber: new FormControl('', { nonNullable: true }),
    equipmentType: new FormControl('', { nonNullable: true }),
    manufacturedAt: new FormControl<Moment | null>(null),
  });

  private applyOwnerEditability() {
    if (this.disabled) {
      return;
    }
    const { companyId, location } = this.baseFormGroup.controls;
    if (!this._editableCompany) {
      companyId.disable({ emitEvent: false });
      location.disable({ emitEvent: false });
      return;
    }
    companyId.enable({ emitEvent: false });
    // A location can only be picked among the chosen company's.
    if (companyId.value != null) {
      location.enable({ emitEvent: false });
    }
  }

  private get classFormValid(): boolean {
    return (
      this.assetClass !== 'STANDALONE_EQUIPMENT' ||
      this.equipmentFormGroup.valid
    );
  }

  private get classFormDirty(): boolean {
    return (
      this.assetClass === 'STANDALONE_EQUIPMENT' &&
      this.equipmentFormGroup.dirty
    );
  }

  private get classFormValue() {
    if (this.assetClass === 'STANDALONE_EQUIPMENT') {
      const eq = this.equipmentFormGroup.getRawValue();
      return {
        ...eq,
        manufacturedAt: toISOString(eq.manufacturedAt),
      };
    }
    return {};
  }

  protected assetClass?: AssetClass;
  protected customFields: CustomField[] = [];
  protected customFormGroup = new FormGroup({
    fields: new UntypedFormArray([]),
  });
}

function toISOString(m: Moment | null | undefined): string | null {
  return m == null ? null : m.toISOString();
}

type FormSection =
  | 'general'
  | 'ownershipAndLocation'
  | 'equipmentDetails'
  | 'installationAndWarranty'
  | 'customFieldsSection'
  | 'attachments';

interface CustomField extends AssetTypeField {
  readonly: boolean;
}

function outOfRange(
  ctl: AbstractControl,
  field: AssetTypeField,
): ValidationErrors | null {
  if (field.min == null && field.max == null) {
    return null;
  }
  const value = parseNumericInput(ctl.value);
  if (value == null) {
    return null;
  }
  return (field.min != null && value < Number(field.min)) ||
    (field.max != null && value > Number(field.max))
    ? { outOfRange: true }
    : null;
}
